/* LOKT 2026-08-27 — DO NOT REFERENCE THE VENDOR `debounce` GLOBAL DIRECTLY HERE.

   `debounce` is defined only in the third-party bundle at js.shrinetheme.com,
   which privacy blockers drop by default: Brave Shields, uBlock Origin, some
   corporate DNS filters. Khalid reported PDP thumbnails doing nothing on every
   product; reproduced by blocking that one request. The old line

     this.elements.viewer.addEventListener('slideChanged', debounce(...))

   sat ABOVE the thumbnail click binding in this constructor, so when the bundle
   was blocked it threw `ReferenceError: debounce is not defined`, the
   constructor aborted, and the click handlers on lines below it never bound.
   The gallery then looked completely dead — clicks reached button.thumbnail and
   nothing happened — for every blocked visitor, on every product, permanently.

   Two guards below, keep both:
   1. loktDebounce() falls back to a local implementation when the vendor global
      is absent. `typeof` on an undeclared identifier is safe and never throws.
   2. The thumbnail click binding now runs FIRST, so any later failure in this
      constructor can no longer cost the gallery its clicks. */
function loktDebounce(fn, wait) {
  if (typeof debounce === 'function') return debounce(fn, wait);
  var timer;
  return function () {
    var ctx = this, args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      /* Clicks first: this is the gallery's only desktop input path. */
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const trigger = mediaToSwitch.querySelector('button');
        if (trigger) trigger.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      this.elements.viewer.addEventListener('slideChanged', loktDebounce(this.onSlideChanged.bind(this), 500));
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

      /* Square galleries 2026-09-27: the MIX quantity-ladder photo sync that stood here is
         deleted. It was dead (templates/product.mix.json has had no quantity-breaks block since
         the launch buy box) and matched colour-block filenames with image.src.includes. The MIX
         flavour jump lives in assets/lokt-launch-buybox.js paintGallery. If a ladder sync is ever
         revived, read the gallery's data-lokt-variant-files and compare data-lokt-file with ===. */
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null, preserveScroll = false) {
      // A Files gallery (data-lokt-files) has no data-alt on its slides, so variant image
      // filtering would hide every slide. It never filters (fix round 1, 2026-09-27): the flavour
      // repaint in assets/lokt-launch-buybox.js owns its slide list.
      if (filtering && currentVariant && !this.hasAttribute('data-lokt-files')) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      // LOAD-BEARING, keep. A Files gallery (square galleries 2026-09-27) keys its slides by
      // filename, so Shrine's variant jump to '<section>-<numeric featured_media id>' finds no
      // slide and must stop here: the slide list and the active slide stay as they are.
      if (!activeMedia) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!preserveScroll && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

      if (this.elements.thumbnails.vertical) {
        console.log('setActiveThumbnail')
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      /* LOKT a11y 2026-09-23: a track made focusable in product-media-gallery
         (zoom off, nothing else to tab to) must keep a real role; presentation
         on a focusable element is ignored and flagged (axe
         presentation-role-conflict). A named group keeps the slides flat. */
      const focusable = this.elements.viewer.slider.hasAttribute('tabindex');
      this.elements.viewer.slider.setAttribute('role', focusable ? 'group' : 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
